import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { languages, LanguageCode } from '@/i18n'
import { useAuth } from '@/contexts/AuthContext'
import apiService from '@/services/api'

interface Language {
  code: LanguageCode
  name: string
  nativeName: string
  flag: string
  isRTL: boolean
}

interface LanguageContextType {
  currentLanguage: LanguageCode
  availableLanguages: Language[]
  changeLanguage: (lng: LanguageCode) => Promise<void>
  isRTL: boolean
  isLoading: boolean
  isLanguageLocked: boolean
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation()
  const { user, organization } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
    (i18n.language as LanguageCode) || 'en'
  )

  // Organizations can enforce a language for all their users.
  // The API returns it flattened as `language`; older payloads nested it in `settings`.
  const org = organization ?? user?.organization
  const orgLanguage = org?.language ?? org?.settings?.language ?? null
  const isLanguageLocked = orgLanguage === 'en' || orgLanguage === 'es'

  // Update current language when i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng as LanguageCode)
    }

    i18n.on('languageChanged', handleLanguageChange)
    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n])

  // Force the organization's language when locked
  useEffect(() => {
    if (isLanguageLocked && orgLanguage && i18n.language !== orgLanguage) {
      i18n.changeLanguage(orgLanguage)
      localStorage.setItem('user-language', orgLanguage)
    }
  }, [isLanguageLocked, orgLanguage, i18n, i18n.language])

  // Apply the user's saved locale (org lock wins; localStorage already applied by the detector)
  useEffect(() => {
    if (isLanguageLocked) return
    const userLocale = user?.locale
    if ((userLocale === 'en' || userLocale === 'es') && i18n.language !== userLocale) {
      i18n.changeLanguage(userLocale)
      localStorage.setItem('user-language', userLocale)
    }
    // Only react to identity/locale changes, not every i18n change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.locale, isLanguageLocked])

  const changeLanguage = async (lng: LanguageCode) => {
    if (isLanguageLocked) return
    if (lng === currentLanguage) return

    setIsLoading(true)
    try {
      await i18n.changeLanguage(lng)

      // Save language preference to localStorage
      localStorage.setItem('user-language', lng)

      // Persist to the user's profile so the preference follows them across devices
      if (user) {
        apiService.updateMe({ locale: lng }).catch(() => {
          // Non-fatal: the local preference still applies
        })
      }

      setCurrentLanguage(lng)
    } catch (error) {
      console.error('Failed to change language:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Get current language details
  const getCurrentLanguage = () => {
    return languages.find((lang) => lang.code === currentLanguage) || languages[0]
  }

  const contextValue: LanguageContextType = {
    currentLanguage,
    availableLanguages: [...languages],
    changeLanguage,
    isRTL: getCurrentLanguage().isRTL,
    isLoading,
    isLanguageLocked,
  }

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

// Convenience hook that combines useTranslation with language context
export const useAppTranslation = (namespace?: string | string[]) => {
  const { t, i18n, ready } = useTranslation(namespace)
  const { currentLanguage, changeLanguage, isRTL, isLoading } = useLanguage()

  return {
    t,
    i18n,
    ready,
    currentLanguage,
    changeLanguage,
    isRTL,
    isLoading,
    // Helper functions
    formatDate: (date: Date) => {
      return new Intl.DateTimeFormat(currentLanguage).format(date)
    },
    formatDateTime: (date: Date) => {
      return new Intl.DateTimeFormat(currentLanguage, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },
    formatFileSize: (bytes: number) => {
      const units = ['bytes', 'KB', 'MB', 'GB', 'TB']
      let size = bytes
      let unitIndex = 0

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      const formatter = new Intl.NumberFormat(currentLanguage, {
        maximumFractionDigits: unitIndex === 0 ? 0 : 1,
      })

      return `${formatter.format(size)} ${t(`units.${units[unitIndex].toLowerCase()}`, { ns: 'common' })}`
    },
    formatNumber: (number: number) => {
      return new Intl.NumberFormat(currentLanguage).format(number)
    },
  }
}
